import Logout from '@mui/icons-material/Logout';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmSheet } from '../../components/ConfirmSheet';
import type { ApiError } from '../../lib/api/api-client';

import { ReasonAlert } from './ReasonAlert';

/** Props of {@link LogoutSheet}. */
export interface LogoutSheetProps {
  readonly open: boolean;
  /** N of S8 R2: chosen channels. */
  readonly channels: number;
  /** M of S8 R2: open FXMate positions. */
  readonly positions: number;
  readonly busy: boolean;
  /** The last logout failure, shown in the sheet (S8 R21). */
  readonly error: ApiError | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/**
 * The logout confirmation (S8 R2, R3, R5; mockup `CompteDeconnexion.html`): says, before anything
 * is sent, that FXMate stops reading the channels, that all copying stops, what happens to the
 * positions (Q31) and how to resume (Q112).
 */
export function LogoutSheet({
  open,
  channels,
  positions,
  busy,
  error,
  onConfirm,
  onCancel,
}: LogoutSheetProps): ReactElement {
  const { t } = useTranslation();
  return (
    <ConfirmSheet
      open={open}
      title={t('account.logoutSheet.title')}
      confirmLabel={t('account.logoutSheet.confirm')}
      cancelLabel={t('account.cancel')}
      icon={<Logout />}
      busy={busy}
      footer={error === null ? undefined : <ReasonAlert error={error} />}
      onConfirm={() => {
        onConfirm();
      }}
      onCancel={onCancel}
    >
      <Stack spacing={1}>
        <Typography>{t('account.logoutSheet.noRead')}</Typography>
        <Typography sx={{ fontWeight: 600 }}>
          {t('account.logoutSheet.stops', { count: channels })}
        </Typography>
        <Typography>{t('account.openPositions', { count: positions })}</Typography>
        <Typography>{t('account.logoutSheet.resume')}</Typography>
      </Stack>
    </ConfirmSheet>
  );
}
